<!--
  @component DomainAggregate — cross-project domain routing (scope = all / many).
  tier:   deployments composable-resource view (1:N routing)

  Phase 3: domains as routing resources. A domain points at ONE app; an app can
  serve MANY domains. Cards ⇄ Matrix over a domain×project grid where each
  domain row has AT MOST ONE routed project (click a cell to route / move /
  unroute — domain.attach moves it). "+ Add domain" opens the BYO add+route wizard.
  reads: useDeploymentIO (domain.list / domain.attach / domain.detach),
         useIdentityIO (projects), useScopeIO (which projects are columns)
-->
<script lang="ts">
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useDeploymentIO } from "../../../../global/io/nodes/deployment/DeploymentIO.svelte";
  import { useScopeIO } from "./ScopeIO.svelte";
  import { scopeProjects } from "./fleet-util.js";
  import { initial, projectGradient } from "../../home/home-util.js";
  import DomainCreateWizard from "./DomainCreateWizard.svelte";

  const identity = useIdentityIO();
  const node = useDeploymentIO();
  const scope = useScopeIO();

  interface DomainRow {
    domain: string;
    vendor: string;
    byo: boolean;
    expiresAtMs: number;
    routedToProjectId: string | null;
    routedToProjectName: string | null;
  }

  const projects = $derived(scopeProjects(identity.projects, scope.projectIds));

  let owned = $state<DomainRow[]>([]);
  let loaded = $state(false);
  let loadError = $state<string | null>(null);
  let cellBusy = $state<string>("");
  let view = $state<"cards" | "matrix">("cards");
  let wizardOpen = $state(false);

  async function loadAll(): Promise<void> {
    try {
      owned = (await node.dispatch("domain.list")) as DomainRow[] ?? [];
      loadError = null;
    } catch (e) {
      owned = [];
      loadError = e instanceof Error ? e.message : String(e);
    } finally {
      loaded = true;
    }
  }
  $effect(() => { void loadAll(); });

  function isRouted(d: DomainRow, projId: string): boolean { return d.routedToProjectId === projId; }

  async function toggle(d: DomainRow, projId: string): Promise<void> {
    if (cellBusy) return;
    cellBusy = `${d.domain}:${projId}`;
    try {
      if (isRouted(d, projId)) await node.dispatch("domain.detach", { target: { id: projId }, domain: d.domain });
      else await node.dispatch("domain.attach", { target: { id: projId }, domain: d.domain });
      await loadAll();
    } catch (e) {
      loadError = e instanceof Error ? e.message : String(e);
    } finally {
      cellBusy = "";
    }
  }
</script>

<div class="agg">
  <header>
    <div class="title">
      <h1>Domains</h1>
      <p class="lede">Every domain you own and the app it serves — a domain routes to one app; an app can serve many. Click a cell to route, move, or unroute.</p>
    </div>
    <div class="tools">
      <div class="seg" role="tablist" aria-label="View">
        <button type="button" role="tab" aria-selected={view === "cards"} class:on={view === "cards"} onclick={() => (view = "cards")}>Cards</button>
        <button type="button" role="tab" aria-selected={view === "matrix"} class:on={view === "matrix"} onclick={() => (view = "matrix")}>Matrix</button>
      </div>
      <button type="button" class="btn-primary" onclick={() => (wizardOpen = true)}>+ Add domain</button>
    </div>
  </header>

  {#if loadError}<p class="err">{loadError}</p>{/if}

  {#if !loaded}
    <p class="muted">Loading domains…</p>
  {:else if owned.length === 0}
    <div class="empty-card">
      <p class="empty-title">No domains yet</p>
      <p class="empty-help">Add a domain you own and route it to any of your projects — one app can serve several.</p>
      <button type="button" class="btn-primary" onclick={() => (wizardOpen = true)}>+ Add domain</button>
    </div>
  {:else if view === "cards"}
    <div class="cards">
      {#each owned as d (d.domain)}
        <section class="dbcard">
          <div class="card-head">
            <span class="mark dom">{initial(d.domain)}</span>
            <div class="card-id">
              <span class="card-name" title={d.domain}>{d.domain}</span>
              <span class="card-meta">{d.byo ? "BYO" : d.vendor}</span>
            </div>
            <span class="pill" class:routed={!!d.routedToProjectId}>{d.routedToProjectId ? "Routed" : "Unrouted"}</span>
          </div>
          <div class="used">
            {#if d.routedToProjectId}
              <span class="used-label">Serves</span>
              <div class="chips"><span class="chip">{d.routedToProjectName}</span></div>
            {:else}
              <span class="muted">Not routed to a project yet — switch to Matrix to route it.</span>
            {/if}
          </div>
        </section>
      {/each}
    </div>
  {:else}
    {#if projects.length === 0}
      <p class="muted">No projects in scope. Widen the scope to route domains.</p>
    {:else}
      <div class="matrix-wrap">
        <table class="matrix">
          <thead>
            <tr>
              <th class="corner">domain \ project</th>
              {#each projects as p (p.id)}
                <th class="col" title={p.name}><span class="cmark" style="background: {projectGradient(p.id)}">{initial(p.name)}</span></th>
              {/each}
            </tr>
          </thead>
          <tbody>
            {#each owned as d (d.domain)}
              <tr>
                <th class="rowlabel" title={d.domain}>
                  <span class="mark dom sm">{initial(d.domain)}</span>
                  <span class="rl-name">{d.domain}</span>
                </th>
                {#each projects as p (p.id)}
                  {@const on = isRouted(d, p.id)}
                  {@const busy = cellBusy === `${d.domain}:${p.id}`}
                  <td>
                    <button
                      type="button"
                      class="cell"
                      class:on={on}
                      class:busy={busy}
                      disabled={cellBusy !== "" && !busy}
                      title={`${on ? "Unroute" : "Route"} ${d.domain} → ${p.name}`}
                      aria-pressed={on}
                      onclick={() => void toggle(d, p.id)}
                    >
                      <span class="dot"></span>
                    </button>
                  </td>
                {/each}
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      <p class="legend"><span class="dot on"></span> serves &nbsp;·&nbsp; <span class="dot"></span> not routed &nbsp;·&nbsp; a domain routes to one app — clicking moves it</p>
    {/if}
  {/if}
</div>

<DomainCreateWizard open={wizardOpen} onClose={() => (wizardOpen = false)} onCreated={() => void loadAll()} />

<style>
  .agg { display: flex; flex-direction: column; gap: var(--space-4); max-width: 1180px; font-family: var(--type-font); }
  header { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--space-4); flex-wrap: wrap; }
  .title { display: flex; flex-direction: column; gap: var(--space-1); min-width: 0; }
  h1 { margin: 0; font-size: 22px; font-weight: 600; letter-spacing: -0.02em; color: var(--text-primary); }
  .lede { margin: 0; font-size: 13.5px; color: var(--text-secondary); line-height: 1.55; max-width: 660px; }
  .tools { display: flex; align-items: center; gap: var(--space-2); flex-shrink: 0; }
  .muted { font-size: 13px; color: var(--text-tertiary); }
  .err { margin: 0; font-size: 12.5px; color: var(--error); }

  .seg { display: inline-flex; border: 0.5px solid var(--border); border-radius: var(--radius-md); overflow: hidden; }
  .seg button { appearance: none; background: transparent; border: 0; padding: 6px var(--space-3); font: inherit; font-size: 12.5px; color: var(--text-secondary); cursor: pointer; transition: background var(--ease-fast); }
  .seg button.on { background: var(--bg-active); color: var(--text-primary); }
  .seg button:not(.on):hover { background: var(--bg-hover); }

  .btn-primary { appearance: none; border-radius: var(--radius-md); padding: 0 var(--space-4); height: 34px; background: var(--bg-active); color: var(--text-primary); border: 0.5px solid var(--border-strong); font: inherit; font-size: 13px; font-weight: 500; cursor: pointer; white-space: nowrap; transition: background var(--ease-fast), border-color var(--ease-fast); }
  .btn-primary:hover { background: var(--bg-selected); border-color: var(--border-focus); }

  .empty-card { display: flex; flex-direction: column; align-items: flex-start; gap: var(--space-2); padding: var(--space-6); background: var(--glass-fill); border: 0.5px solid var(--border); border-radius: var(--radius-xl, 18px); }
  .empty-title { margin: 0; font-size: 15px; font-weight: 600; color: var(--text-primary); }
  .empty-help { margin: 0 0 var(--space-2); font-size: 13px; color: var(--text-secondary); }

  .cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: var(--space-3); }
  .dbcard { display: flex; flex-direction: column; gap: var(--space-3); padding: var(--space-4); background: var(--glass-fill); border: 0.5px solid var(--border); border-radius: var(--radius-lg, 14px); }
  .card-head { display: flex; align-items: center; gap: var(--space-2); }
  .mark { width: 26px; height: 26px; border-radius: var(--radius-sm); flex-shrink: 0; display: grid; place-items: center; font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.92); }
  .mark.dom { background: linear-gradient(135deg, #7b5bff, #b85bff); }
  .mark.sm { width: 20px; height: 20px; font-size: 10px; }
  .card-id { display: flex; flex-direction: column; min-width: 0; flex: 1; }
  .card-name { font-size: 14px; font-weight: 600; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .card-meta { font-size: 11.5px; color: var(--text-tertiary); }
  .pill { font-size: 11px; font-weight: 500; padding: 2px var(--space-2); border-radius: var(--radius-pill); background: var(--bg-hover); color: var(--text-tertiary); white-space: nowrap; }
  .pill.routed { background: color-mix(in srgb, var(--success) 16%, transparent); color: var(--success); }
  .used { display: flex; flex-direction: column; gap: var(--space-2); }
  .used-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-tertiary); }
  .chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .chip { font-size: 12px; padding: 3px var(--space-2); border-radius: var(--radius-pill); background: var(--bg-hover); color: var(--text-secondary); border: 0.5px solid var(--border-subtle); }

  .matrix-wrap { overflow-x: auto; border: 0.5px solid var(--border); border-radius: var(--radius-lg, 14px); background: var(--glass-fill); scrollbar-width: thin; }
  .matrix { border-collapse: collapse; width: 100%; }
  .matrix th, .matrix td { padding: 0; }
  .corner { position: sticky; left: 0; z-index: 2; background: var(--glass-fill); text-align: left; padding: var(--space-2) var(--space-3); font-size: 10.5px; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-ghost); white-space: nowrap; }
  .col { padding: var(--space-2) 6px; }
  .cmark { width: 22px; height: 22px; border-radius: var(--radius-sm); display: inline-grid; place-items: center; font-size: 10px; font-weight: 600; color: rgba(255,255,255,0.92); }
  .rowlabel { position: sticky; left: 0; z-index: 1; background: var(--glass-fill); display: flex; align-items: center; gap: var(--space-2); padding: var(--space-2) var(--space-3); border-top: 0.5px solid var(--border-subtle); max-width: 240px; }
  .rl-name { font-size: 13px; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-family: var(--type-mono); }
  .matrix tbody tr td { border-top: 0.5px solid var(--border-subtle); text-align: center; }
  .cell { appearance: none; background: transparent; border: 0; width: 100%; height: 38px; display: grid; place-items: center; cursor: pointer; transition: background var(--ease-fast); }
  .cell:hover:not(:disabled) { background: var(--bg-hover); }
  .cell:disabled { cursor: not-allowed; }
  .cell .dot { width: 13px; height: 13px; border-radius: var(--radius-pill); border: 1.5px solid var(--border-strong); background: transparent; transition: background var(--ease-fast), border-color var(--ease-fast); }
  .cell.on .dot { background: var(--success); border-color: var(--success); }
  .cell.busy .dot { opacity: 0.5; }
  .legend { display: flex; align-items: center; gap: 4px; margin: 0; font-size: 11.5px; color: var(--text-tertiary); }
  .legend .dot { width: 11px; height: 11px; border-radius: var(--radius-pill); border: 1.5px solid var(--border-strong); display: inline-block; vertical-align: middle; }
  .legend .dot.on { background: var(--success); border-color: var(--success); }
</style>
